import { word } from "@/modules/core/utils/i18n"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import type { ProgramType, ProgramVisibility } from "@/modules/programs/types/programTypes"
import { SessionBlockEntityFactory, type SessionBlockEntityType } from "@/modules/sessions/entities/sessionBlockEntity"
import type { OnOffSessionDetailsDto, OnOffSessionInputDto } from "@/modules/sessions/gateway/programOnOffSessionDto"
import type { ProgramOnOffSessionFormikType } from "@/modules/sessions/programSessionFormikType"
import { ProgramOnOffSessionTypeOf } from "@/modules/sessions/programSessionTypes"
import { SocialPostResumeLightEntity } from "@/modules/social/entities/socialEntity"

export class ProgramOnOffSessionEntity {
  id: string
  name: string
  description: string | null
  date: string | null
  owner: UserEntity
  programId: string
  programName: string
  programVisibility: ProgramVisibility
  hideSessionContent: boolean | null
  weightliftingTag: boolean
  gymnasticTag: boolean
  cardioTag: boolean
  restDay: boolean
  index: number | null
  programType: ProgramType
  socialPostId: string | null
  socialResume: SocialPostResumeLightEntity | null
  blocks: SessionBlockEntityType[]
  sessionCompletedByUser: boolean

  private constructor(programOnOffSession: ProgramOnOffSessionEntity) {
    this.id = programOnOffSession.id
    this.name = programOnOffSession.name
    this.description = programOnOffSession.description
    this.date = programOnOffSession.date
    this.owner = programOnOffSession.owner
    this.programId = programOnOffSession.programId
    this.programName = programOnOffSession.programName
    this.programVisibility = programOnOffSession.programVisibility
    this.hideSessionContent = programOnOffSession.hideSessionContent
    this.weightliftingTag = programOnOffSession.weightliftingTag
    this.gymnasticTag = programOnOffSession.gymnasticTag
    this.cardioTag = programOnOffSession.cardioTag
    this.restDay = programOnOffSession.restDay
    this.index = programOnOffSession.index
    this.programType = programOnOffSession.programType
    this.socialPostId = programOnOffSession.socialPostId
    this.socialResume = programOnOffSession.socialResume
    this.blocks = programOnOffSession.blocks
    this.sessionCompletedByUser = programOnOffSession.sessionCompletedByUser
  }

  static fromDto(dto: OnOffSessionDetailsDto): ProgramOnOffSessionEntity {
    return new ProgramOnOffSessionEntity({
      id: dto.id,
      name: dto.name,
      description: dto.description,
      date: dto.date,
      owner: UserEntity.fromDto(dto.owner),
      programId: dto.programId,
      programName: dto.programName,
      programVisibility: dto.programVisibility,
      hideSessionContent: dto.hideSessionContent,
      weightliftingTag: dto.weightliftingTag,
      gymnasticTag: dto.gymnasticTag,
      cardioTag: dto.cardioTag,
      restDay: dto.restDay,
      index: dto.index,
      programType: dto.programType,
      socialPostId: dto.socialPostId,
      socialResume: dto.socialResume ? SocialPostResumeLightEntity.fromDto(dto.socialResume) : null,
      blocks: dto.blocks.map((block) => SessionBlockEntityFactory.fromDto(block)),
      sessionCompletedByUser: dto.sessionCompletedByUser,
    })
  }

  static fromEntityToFormik(entity: ProgramOnOffSessionEntity): ProgramOnOffSessionFormikType {
    return {
      name: entity.name,
      programId: entity.programId,
      dayIndex: entity.index !== null ? entity.index + 1 : null,
      restDay: entity.restDay,
      description: entity.description,
      blocks: entity.blocks.map((block) => SessionBlockEntityFactory.fromEntityToFormik(block)),
      hideSessionContent: entity.hideSessionContent ?? false,
      cardioTag: entity.cardioTag,
      weightliftingTag: entity.weightliftingTag,
      gymnasticTag: entity.gymnasticTag,
      _type: ProgramOnOffSessionTypeOf,
      isTemplateSaved: false,
    }
  }

  static getDraftFormik(programId: string, dayIndex: number | string | undefined): ProgramOnOffSessionFormikType {
    return {
      name: word("program.session.name.default"),
      _type: ProgramOnOffSessionTypeOf,
      description: "",
      blocks: [],
      programId: programId,
      hideSessionContent: false,
      cardioTag: false,
      gymnasticTag: false,
      weightliftingTag: false,
      dayIndex: typeof dayIndex === "number" ? dayIndex + 1 : null,
      restDay: false,
      isTemplateSaved: false,
    }
  }

  static fromEntityToInputDto(entity: ProgramOnOffSessionEntity): OnOffSessionInputDto {
    return {
      name: entity.name,
      description: entity.description,
      duration: 0,
      blocks: entity.blocks.map((block) => SessionBlockEntityFactory.fromEntityToInputDto(block)),
      hideSessionContent: entity.hideSessionContent ?? false,
      weightliftingTag: entity.weightliftingTag,
      gymnasticTag: entity.gymnasticTag,
      cardioTag: entity.cardioTag,
      restDay: entity.restDay,
      index: entity.index ?? 0,
    }
  }

  static fromFormikToInputDto(formik: ProgramOnOffSessionFormikType): OnOffSessionInputDto {
    return {
      name: formik.name,
      description: formik.description,
      duration: 0,
      blocks: formik.blocks.map((block, index) => SessionBlockEntityFactory.fromFormikToInputDto(block, index)),
      hideSessionContent: formik.hideSessionContent,
      weightliftingTag: formik.weightliftingTag,
      gymnasticTag: formik.gymnasticTag,
      cardioTag: formik.cardioTag,
      restDay: formik.restDay,
      index: formik.dayIndex !== null ? formik.dayIndex - 1 : 0,
    }
  }
}
